import { RNG } from './RNG';
import {
    SymbolType,
    SpinResult,
    WinLine,
    GameConfig,
    GameState,
    ReelStrip,
    FeatureConfig
} from '../types/game.types';
import { DEFAULT_PAYTABLE, PAYLINES_5x3 } from '../config/paytable.config';
import { DEFAULT_REEL_STRIPS } from '../config/reels.config';
import { SYMBOLS } from '../config/symbols.config';
import { UserManager } from '../components/UserManager';

export class GameEngine {
    private rng: RNG;
    private config: GameConfig;
    private state: GameState;
    private reelStrips: ReelStrip[];
    private paytable: any;
    private features: FeatureConfig;
    
    constructor(seed?: number) {
        this.rng = new RNG(seed);
        
        this.config = {
            reels: 5,
            rows: 3,
            paylines: PAYLINES_5x3,
            minBet: 1,
            maxBet: 100,
            defaultBet: 1,
            targetRTP: 0.96,
            hitRate: 0.28,
            volatility: 'medium-high'
        };
        
        this.state = {
            balance: 1000,
            betPerLine: 1,
            activeLines: 20,
            totalBet: 20,
            isSpinning: false,
            isAutoplay: false,
            autoplayRemaining: 0,
            freeSpinsRemaining: 0,
            freeSpinsMultiplier: 1,
            turboMode: false,
            lastWin: 0,
            sessionWins: 0,
            sessionLosses: 0
        };
        
        this.reelStrips = DEFAULT_REEL_STRIPS;
        this.paytable = DEFAULT_PAYTABLE;
        
        this.features = {
            freeSpins: {
                triggerCount: 3,
                spinsAwarded: 10,
                multiplier: 3,
                retriggerAllowed: true,
                triggerChance: 0.01
            },
            randomWild: {
                chance: 0.1,
                minWilds: 2,
                maxWilds: 5
            },
            megaSymbol: {
                chance: 0.0,  // Disabled - was causing too many duplicate symbols
                size: 3,
                eligibleSymbols: [
                    SymbolType.TREASURE_CHEST,
                    SymbolType.NEON_DIAMOND,
                    SymbolType.SHOWGIRL_HAT,
                    SymbolType.SPORTS_CAR
                ]
            }
        };
    }
    
    spin(): SpinResult {
        const reels = this.generateReels();
        const triggeredFeatures: string[] = [];
        
        if (this.state.freeSpinsRemaining === 0) {
            if (this.rng.chance(this.features.megaSymbol.chance)) {
                this.applyMegaSymbol(reels);
                triggeredFeatures.push('mega_symbol');
            }
            
            const scatterPreCheck = this.countScatters(reels);
            if (scatterPreCheck < 2 && this.rng.chance(this.features.randomWild.chance)) {
                this.applyRandomWilds(reels);
                triggeredFeatures.push('random_wilds');
            }
        }
        
        const wins = this.calculateWins(reels);
        const scatterCount = this.countScatters(reels);
        let totalWin = wins.reduce((sum, win) => sum + win.payout, 0);
        
        if (scatterCount >= 3) {
            const scatterWin = this.calculateScatterWin(scatterCount);
            totalWin += scatterWin;
            
            if (this.state.freeSpinsRemaining === 0) {
                this.state.freeSpinsRemaining = this.features.freeSpins.spinsAwarded;
                this.state.freeSpinsMultiplier = this.features.freeSpins.multiplier;
                triggeredFeatures.push('free_spins');
            } else if (this.features.freeSpins.retriggerAllowed) {
                this.state.freeSpinsRemaining += this.features.freeSpins.spinsAwarded;
                triggeredFeatures.push('free_spins_retrigger');
            }
        }
        
        if (this.state.freeSpinsRemaining > 0) {
            totalWin *= this.state.freeSpinsMultiplier;
            this.state.freeSpinsRemaining--;
        }
        
        this.state.balance += totalWin;
        this.state.lastWin = totalWin;

        if (totalWin > 0) {
            // Sync winnings with user manager
            this.syncBalance(totalWin, 0);
        }
        
        if (totalWin > 0) {
            this.state.sessionWins += totalWin;
        } else {
            this.state.sessionLosses += this.state.totalBet;
        }
        
        return {
            reels,
            wins,
            totalWin,
            scatterCount,
            triggeredFeatures
        };
    }
    
    private generateReels(): SymbolType[][] {
        const reels: SymbolType[][] = [];
        
        for (let r = 0; r < this.config.reels; r++) {
            const strip = this.reelStrips[r].symbols;
            const startPos = this.rng.randomInt(0, strip.length - 1);
            const reelSymbols: SymbolType[] = [];
            
            for (let row = 0; row < this.config.rows; row++) {
                const pos = (startPos + row) % strip.length;
                reelSymbols.push(strip[pos]);
            }
            
            reels.push(reelSymbols);
        }
        
        return reels;
    }
    
    private calculateWins(reels: SymbolType[][]): WinLine[] {
        const wins: WinLine[] = [];
        
        // Only check active lines
        for (let lineIndex = 0; lineIndex < this.state.activeLines; lineIndex++) {
            const line = this.config.paylines[lineIndex];
            const lineSymbols: SymbolType[] = [];
            const positions: { reel: number; row: number }[] = [];
            
            for (let i = 0; i < line.length; i++) {
                const [row, reel] = line[i];
                lineSymbols.push(reels[reel][row]);
                positions.push({ reel, row });
            }
            
            const win = this.checkLineWin(lineSymbols, positions, lineIndex + 1);
            if (win) {
                wins.push(win);
            }
        }
        
        return wins;
    }
    
    private checkLineWin(symbols: SymbolType[], positions: { reel: number; row: number }[], lineNumber: number): WinLine | null {
        let matchSymbol = symbols[0];
        let matchCount = 1;
        
        if (SYMBOLS[matchSymbol].isWild) {
            for (let i = 1; i < symbols.length; i++) {
                if (!SYMBOLS[symbols[i]].isWild && !SYMBOLS[symbols[i]].isScatter) {
                    matchSymbol = symbols[i];
                    break;
                }
            }
        }
        
        for (let i = 1; i < symbols.length; i++) {
            const currentSymbol = symbols[i];
            
            if (currentSymbol === matchSymbol || SYMBOLS[currentSymbol].isWild) {
                matchCount++;
            } else {
                break;
            }
        }
        
        if (matchCount >= 3 && !SYMBOLS[matchSymbol].isScatter) {
            const payout = this.getSymbolPayout(matchSymbol, matchCount);
            if (payout > 0) {
                return {
                    lineNumber,
                    symbols: symbols.slice(0, matchCount),
                    positions: positions.slice(0, matchCount),
                    payout: payout * this.state.betPerLine,
                    symbolCount: matchCount
                };
            }
        }
        
        return null;
    }
    
    private getSymbolPayout(symbol: SymbolType, count: number): number {
        const payouts = this.paytable[symbol];
        if (!payouts) return 0;
        
        switch (count) {
            case 3: return payouts[3] || 0;
            case 4: return payouts[4] || 0;
            case 5: return payouts[5] || 0;
            default: return 0;
        }
    }
    
    private countScatters(reels: SymbolType[][]): number {
        let count = 0;
        for (const reel of reels) {
            for (const symbol of reel) {
                if (symbol === SymbolType.SCATTER) {
                    count++;
                }
            }
        }
        return count;
    }
    
    private calculateScatterWin(count: number): number {
        const scatterPayouts = this.paytable[SymbolType.SCATTER];
        let multiplier = 0;
        
        switch (count) {
            case 3: multiplier = scatterPayouts[3] || 2; break;
            case 4: multiplier = scatterPayouts[4] || 10; break;
            case 5: multiplier = scatterPayouts[5] || 50; break;
            default: multiplier = scatterPayouts[5] || 50; break;
        }
        
        return this.state.totalBet * multiplier;
    }
    
    private applyRandomWilds(reels: SymbolType[][]): void {
        const wildCount = this.rng.randomInt(
            this.features.randomWild.minWilds,
            this.features.randomWild.maxWilds
        );
        
        for (let i = 0; i < wildCount; i++) {
            const reel = this.rng.randomInt(0, this.config.reels - 1);
            const row = this.rng.randomInt(0, this.config.rows - 1);
            
            if (reels[reel][row] !== SymbolType.SCATTER) {
                reels[reel][row] = SymbolType.WILD;
            }
        }
    }
    
    private applyMegaSymbol(reels: SymbolType[][]): void {
        const symbol = this.rng.pick(this.features.megaSymbol.eligibleSymbols);
        const startReel = this.rng.randomInt(0, this.config.reels - this.features.megaSymbol.size);
        
        for (let r = startReel; r < startReel + this.features.megaSymbol.size && r < this.config.reels; r++) {
            for (let row = 0; row < this.config.rows; row++) {
                reels[r][row] = symbol;
            }
        }
    }
    
    setBetPerLine(amount: number): void {
        if (amount >= this.config.minBet && amount <= this.config.maxBet) {
            this.state.betPerLine = amount;
            this.updateTotalBet();
        }
    }
    
    setActiveLines(lines: number): void {
        if (lines >= 1 && lines <= this.config.paylines.length) {
            this.state.activeLines = lines;
            this.updateTotalBet();
        }
    }
    
    private updateTotalBet(): void {
        this.state.totalBet = this.state.betPerLine * this.state.activeLines;
    }
    
    deductBet(): boolean {
        if (this.state.balance >= this.state.totalBet) {
            this.state.balance -= this.state.totalBet;
            // Sync bet with user manager
            this.syncBalance(0, this.state.totalBet);
            return true;
        }
        return false;
    }
    
    getState(): GameState {
        return { ...this.state };
    }
    
    setBalance(balance: number): void {
        this.state.balance = balance;
    }

    private syncBalance(won: number, lost: number): void {
        const userManager = UserManager.getInstance();
        userManager.updateBalance(this.state.balance, won, lost).catch(error => {
            console.warn('Failed to sync balance with server:', error);
        });
    }
    
    getConfig(): GameConfig {
        return { ...this.config };
    }
    
    updateConfig(newConfig: Partial<GameConfig>): void {
        this.config = { ...this.config, ...newConfig };
    }
    
    updateFeatures(newFeatures: Partial<FeatureConfig>): void {
        this.features = { ...this.features, ...newFeatures };
    }
    
    setSeed(seed: number): void {
        this.rng.seed(seed);
    }
}